import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BuildIcon from '@mui/icons-material/Build'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import Chip from '@mui/material/Chip'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { type ToolResultDetail } from '@/core/models/medconnect-agent.model'
import { ToolCallDisplay } from './ToolCallDisplay'
import { Trans } from 'react-i18next'
import _ from 'lodash'

interface ToolCallGroupProps {
    items: ToolResultDetail[]
}

export const ToolCallGroup: React.FC<ToolCallGroupProps> = ({ items }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [expanded, setExpanded] = useState(false)

    const allStatuses = useMemo(() => {
        const hasError = items.some((it) => _.get(it.result, 'error', false))
        return hasError ? 'error' : 'success'
    }, [items])

    const statusChip = allStatuses === 'error'
        ? { label: 'Error', color: 'error' as const, icon: <ErrorIcon /> }
        : { label: 'Success', color: 'success' as const, icon: <CheckCircleIcon /> }

    return (
        <Accordion
            sx={{
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                '&:before': { display: 'none' },
            }}
            expanded={expanded}
            onChange={() => setExpanded((v) => !v)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-content': {
                        pl: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexGrow: 1 }}>
                        <BuildIcon fontSize="small" />
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 'medium', minWidth: 0 }}>
                            {items.length} <Trans> tool actions </Trans>
                        </Typography>
                    </Box>
                    <Chip
                        icon={statusChip.icon}
                        label={isMobile ? '' : statusChip.label}
                        color={statusChip.color}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 'auto' }}
                    />
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {items.map((invocation, idx) => (
                    <ToolCallDisplay key={invocation.tool_call_id ? `${invocation.tool_call_id}` : `tool-${idx}`} toolInvocation={invocation} />
                ))}
            </AccordionDetails>
        </Accordion>
    )
}

export default ToolCallGroup

